import { useCallback, useEffect, useState } from 'react';
import { serverCapacityAPI } from '../services/api';

const EMPTY_ASSUMPTIONS = {
  users: 60,
  concurrencyPct: 40,
  cpuPerActiveUser: 1,
  ramGbPerActiveUser: 4,
  storageGbPerAccount: 20,
  headroomPct: 25,
  workloadType: 'Interactive EDA lab',
  tools: '',
  migrateData: false,
  excludedAliases: [],
  maintenanceAliases: [],
};

function useServerCapacity() {
  const [days, setDays] = useState(30);
  const [server, setServer] = useState('');
  const [batch, setBatch] = useState('');
  const [assumptions, setAssumptions] = useState(EMPTY_ASSUMPTIONS);
  const [overrides, setOverrides] = useState({});
  const [report, setReport] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsMeta, setSettingsMeta] = useState(null);
  const [error, setError] = useState('');

  function applySettings(payload) {
    const stored = payload?.assumptions;
    if (stored) {
      setAssumptions({
        ...EMPTY_ASSUMPTIONS,
        ...stored,
        tools: Array.isArray(stored.tools) ? stored.tools.join(', ') : stored.tools || '',
        excludedAliases: stored.excludedAliases || [],
        maintenanceAliases: stored.maintenanceAliases || [],
      });
    }
    setSettingsMeta(payload || null);
  }

  const loadPlans = useCallback(async () => {
    const response = await serverCapacityAPI.plans();
    setPlans(response.data?.plans || []);
  }, []);

  useEffect(() => {
    if (!settingsLoaded) return undefined;
    let cancel = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await serverCapacityAPI.preview({
          days,
          server,
          batch,
          assumptions: {
            ...assumptions,
            tools: String(assumptions.tools || '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          },
          assignments: overrides,
        });
        if (!cancel) setReport(response.data);
      } catch (err) {
        if (!cancel) setError(err?.response?.data?.message || err.message || 'Could not load capacity');
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 250);
    return () => {
      cancel = true;
      clearTimeout(timer);
    };
  }, [settingsLoaded, days, server, batch, assumptions, overrides]);

  useEffect(() => {
    let cancel = false;
    serverCapacityAPI
      .settings()
      .then((response) => {
        if (cancel) return;
        applySettings(response.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancel) setSettingsLoaded(true);
      });
    loadPlans().catch(() => {});
    return () => {
      cancel = true;
    };
  }, [loadPlans]);

  function updateAssumption(key, value) {
    setAssumptions((current) => ({ ...current, [key]: value }));
  }

  function toggleAlias(key, alias) {
    setAssumptions((current) => {
      const list = new Set(current[key] || []);
      if (list.has(alias)) list.delete(alias);
      else list.add(alias);
      return { ...current, [key]: [...list] };
    });
  }

  function setOverride(alias, value) {
    setOverrides((current) => {
      const next = { ...current };
      if (value === '' || value == null) delete next[alias];
      else next[alias] = value;
      return next;
    });
  }

  async function save(existingId) {
    setSaving(true);
    setError('');
    try {
      const body = {
        days,
        server,
        batch,
        assumptions: {
          ...assumptions,
          tools: String(assumptions.tools || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
        },
        assignments: overrides,
      };
      if (existingId) await serverCapacityAPI.update(existingId, body);
      else await serverCapacityAPI.save(body);
      await loadPlans();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not save the plan');
    } finally {
      setSaving(false);
    }
  }

  async function approve(id) {
    setSaving(true);
    setError('');
    try {
      await serverCapacityAPI.approve(id, 'Approved for review. No accounts were created.');
      await loadPlans();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not approve the plan');
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings() {
    setSaving(true);
    setError('');
    try {
      const response = await serverCapacityAPI.saveSettings({
        ...assumptions,
        tools: String(assumptions.tools || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      applySettings(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not save the planner assumptions');
    } finally {
      setSaving(false);
    }
  }

  async function download(id) {
    const response = await serverCapacityAPI.exportCsv(id);
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `capacity-plan-${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return {
    days,
    setDays,
    server,
    setServer,
    batch,
    setBatch,
    assumptions,
    updateAssumption,
    toggleAlias,
    overrides,
    setOverride,
    report,
    plans,
    loading,
    saving,
    settingsMeta,
    error,
    save,
    saveSettings,
    approve,
    download,
  };
}

export default useServerCapacity;
